import { useCallback, useEffect, useRef } from 'react';
import { setOptions, importLibrary } from '@googlemaps/js-api-loader';
import { DEFAULT_MAP_CENTER, GOOGLE_MAPS_API_KEY } from '../constants/centro.constants';

interface UseCentroMapParams {
    activeTab: string;
    lat: string;
    lng: string;
    radio: number;
    onCoordsChange: (lat: string, lng: string) => void;
}

export function useCentroMap({ activeTab, lat, lng, radio, onCoordsChange }: UseCentroMapParams) {
    const mapRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const googleMap = useRef<google.maps.Map | null>(null);
    const mapMarker = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
    const mapCircle = useRef<google.maps.Circle | null>(null);

    const updateMapPosition = useCallback(
        (googleObj?: typeof google) => {
            const g = googleObj ?? window.google;
            if (!googleMap.current || !g?.maps || !lat || !lng) return;

            const pos = { lat: parseFloat(lat), lng: parseFloat(lng) };
            googleMap.current.panTo(pos);

            if (mapMarker.current) {
                mapMarker.current.position = pos;
            } else {
                mapMarker.current = new g.maps.marker.AdvancedMarkerElement({
                    map: googleMap.current,
                    position: pos,
                    gmpDraggable: true,
                });
                mapMarker.current.addListener('dragend', (e: google.maps.MapMouseEvent) => {
                    if (e.latLng) {
                        onCoordsChange(e.latLng.lat().toString(), e.latLng.lng().toString());
                    }
                });
            }

            if (mapCircle.current) {
                mapCircle.current.setCenter(pos);
                mapCircle.current.setRadius(radio);
            } else {
                mapCircle.current = new g.maps.Circle({
                    strokeColor: '#154750',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: '#EE9D4B',
                    fillOpacity: 0.35,
                    map: googleMap.current,
                    center: pos,
                    radius: radio,
                });
            }
        },
        [lat, lng, radio, onCoordsChange],
    );

    useEffect(() => {
        if (activeTab !== 'location') return;

        // El div del mapa no está visible en otras pestañas; inicializar solo al abrir Ubicación
        const initOrUpdate = async () => {
            let g = window.google;
            if (!g?.maps?.places) {
                try {
                    setOptions({ key: GOOGLE_MAPS_API_KEY, libraries: ['maps', 'places', 'marker'] });
                    await Promise.all([
                        importLibrary('core'),
                        importLibrary('maps'),
                        importLibrary('places'),
                        importLibrary('marker'),
                    ]);
                    g = window.google;
                } catch {
                    return;
                }
            }

            setTimeout(() => {
                if (!googleMap.current && mapRef.current && g?.maps) {
                    const pos = {
                        lat: parseFloat(lat) || DEFAULT_MAP_CENTER.lat,
                        lng: parseFloat(lng) || DEFAULT_MAP_CENTER.lng,
                    };
                    googleMap.current = new g.maps.Map(mapRef.current, {
                        center: pos,
                        zoom: 19,
                        mapId: 'centro_map_id',
                    });
                    googleMap.current.addListener('click', (e: google.maps.MapMouseEvent) => {
                        if (e.latLng) {
                            onCoordsChange(e.latLng.lat().toString(), e.latLng.lng().toString());
                        }
                    });

                    if (searchInputRef.current) {
                        const autocomplete = new g.maps.places.Autocomplete(searchInputRef.current);
                        autocomplete.bindTo('bounds', googleMap.current);
                        autocomplete.addListener('place_changed', () => {
                            const place = autocomplete.getPlace();
                            if (place.geometry?.location) {
                                onCoordsChange(
                                    place.geometry.location.lat().toString(),
                                    place.geometry.location.lng().toString(),
                                );
                            }
                        });
                    }
                }
                updateMapPosition(g);
            }, 200);
        };

        initOrUpdate();
    }, [activeTab, lat, lng, onCoordsChange, updateMapPosition]);

    useEffect(() => {
        updateMapPosition();
    }, [lat, lng, radio, updateMapPosition]);

    return { mapRef, searchInputRef };
}
